import React, { useState, useMemo, useEffect } from 'react';

import {
  ShopOutlined,
  DatabaseOutlined,
  HomeOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  RocketOutlined,
  LinkOutlined,
  AppstoreOutlined,
  CarOutlined,
} from '@ant-design/icons';
import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { useIsStandard } from '@contexts/operator/PlanContext';
import { onboardingService } from '@services/operator/onboardingService';
import { getAssetPath } from '@utils/assetPaths';
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Steps,
  message,
  Result,
  Row,
  Col,
  Alert,
  Radio,
  Space,
} from 'antd';
import { useTranslation } from 'react-i18next';

const { Title, Paragraph, Text } = Typography;

// Step 定義：每個 step 對應一個 key 和 icon
const STEP_DEFS = {
  warehouse: { icon: <HomeOutlined /> },
  inventory_type: { icon: <DatabaseOutlined /> },
  merchant: { icon: <ShopOutlined /> },
  complete: { icon: <CheckCircleOutlined /> },
};


const OnboardingWizard = ({ onClose }) => {
  const { t } = useTranslation();
  const { operator } = useOperatorAuth();
  const { merchants, refreshOperatorData } = useOperatorData();

  // Standard plan auto-creates merchant → skip merchant step
  const hasMerchant = merchants.length > 0;
  const STEPS = hasMerchant
    ? ['inventory_type', 'complete']
    : ['inventory_type', 'merchant', 'complete'];
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const onboardingStatus = operator?.onboarding_status || {};
  const isStandard = useIsStandard();
  const organizationName = onboardingStatus.organization_name || '';

  // 根據後端資料動態計算需要的 steps
  // warehouse 步驟永遠顯示（註冊時自動建立預設倉庫，讓使用者在此修改名稱和儲位模式）
  const steps = useMemo(() => {
    const result = ['warehouse'];
    if (!onboardingStatus.has_inventory_types) {
      result.push('inventory_type');
    }
    if (!onboardingStatus.has_merchants) {
      result.push('merchant');
    }
    result.push('complete');
    return result;
  }, [onboardingStatus.has_inventory_types, onboardingStatus.has_merchants]);

  const currentStepKey = steps[currentStep];

  // 預填倉庫資料（註冊時自動建立的預設倉庫）
  useEffect(() => {
    if (operator?.warehouse) {
      form.setFieldsValue({
        warehouse_name: operator.warehouse.title,
        storage_mode: onboardingStatus.warehouse_storage_mode || 'virtual',
      });
    }
  }, [operator?.warehouse, onboardingStatus.warehouse_storage_mode, form]);

  // Standard 方案：預填貨主名稱
  useEffect(() => {
    if (isStandard && organizationName) {
      form.setFieldsValue({ merchant_name: organizationName });
    }
  }, [isStandard, organizationName, form]);

  // 每個 form step 對應要驗證的欄位清單
  const fieldsByStep = {
    warehouse: ['warehouse_name', 'storage_mode'],
    inventory_type: ['inventory_type_code', 'inventory_type_name'],
    merchant: ['merchant_name'],
  };

  // 統一的「下一步」處理：驗證當前 step 的欄位 → 若下一步是 complete 就送 API，否則單純前進
  // Wizard 不支援 resume，所以三步的結果要等到最後一次 submit 原子送出，失敗整批 rollback
  const handleNext = async () => {
    const fields = fieldsByStep[currentStepKey];
    if (!fields) return;

    try {
      await form.validateFields(fields);
    } catch (error) {
      if (error?.errorFields) return;
      throw error;
    }

    const nextStepKey = steps[currentStep + 1];
    if (nextStepKey === 'complete') {
      await submitAll();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  // 把 wizard 收到的所有欄位一次打包送到 /onboarding/complete
  // 未出現在 steps 裡的 section（例如 standard plan 會跳過 merchant）不會被納入 payload
  const submitAll = async () => {
    const values = form.getFieldsValue(true);

    const payload = {};

    if (steps.includes('warehouse')) {
      payload.warehouse = {
        name: values.warehouse_name,
        storage_mode: values.storage_mode || 'virtual',
      };
    }
    if (steps.includes('inventory_type')) {
      payload.inventory_type = {
        code: values.inventory_type_code,
        name: values.inventory_type_name,
      };
    }
    if (steps.includes('merchant')) {
      payload.merchant = { name: values.merchant_name };
    }

    setSubmitting(true);
    try {
      const result = await onboardingService.complete(payload);

      if (result.success !== false && result.data) {
        message.success(t('common.message.create_success'));
        setCurrentStep(prev => prev + 1);
        return;
      }

      // 伺服器端 validation 失敗：顯示第一個錯誤，讓使用者回去修正
      const firstError = result.errors && Object.values(result.errors)[0]?.[0];
      message.error(firstError || result.message || t('common.error.create_failed'));
    } catch (error) {
      console.error('Onboarding submit error:', error);
      message.error(t('common.error.create_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async () => {
    try {
      await refreshOperatorData();
    } catch (_) {
      // ignore
    }
    onClose();
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  const SETTING_LINKS = [
    { icon: <DatabaseOutlined />, labelKey: 'link_inventory_types', path: '/operator/data/inventory_types' },
    { icon: <ShopOutlined />, labelKey: 'link_merchants', path: '/operator/merchants' },
  ];

  const ADVANCED_LINKS = [
    { icon: <CarOutlined />, labelKey: 'link_shipping_accounts', path: '/operator/shipping_accounts?action=new' },
    { icon: <AppstoreOutlined />, labelKey: 'link_shelves', path: '/operator/shelves/batch_new' },
  ];

  // 渲染各個 step 的表單內容
  const renderStepContent = () => {
    switch (currentStepKey) {
      case 'warehouse':
        return (
          <div style={{ animation: 'fadeIn 0.4s' }}>
            <Title level={3} style={{ marginBottom: 4 }}>
              {t('operator.onboarding_wizard.warehouse.heading')}
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 24 }}>
              {t('operator.onboarding_wizard.warehouse.description')}
            </Paragraph>

            <Form.Item
              name="warehouse_name"
              label={t('models.warehouse.name')}
              rules={[{ required: true, message: t('common.validation.required') }]}
              extra={t('operator.onboarding_wizard.warehouse.name_hint')}
            >
              <Input
                placeholder={t('operator.onboarding_wizard.warehouse.name_placeholder')}
                prefix={<HomeOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="storage_mode"
              label={t('operator.onboarding_wizard.warehouse.storage_mode_label')}
              rules={[{ required: true, message: t('common.validation.required') }]}
              initialValue="virtual"
            >
              <Radio.Group style={{ width: '100%' }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {['virtual', 'dedicated', ...(isStandard ? [] : ['dynamic'])].map(mode => (
                    <Radio key={mode} value={mode} style={{ display: 'flex', alignItems: 'flex-start', padding: '12px 16px', border: '1px solid #d9d9d9', borderRadius: 8, width: '100%' }}>
                      <div>
                        <Text strong>{t(`operator.onboarding_wizard.warehouse.storage_mode_${mode}`)}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          {t(`operator.onboarding_wizard.warehouse.storage_mode_${mode}_desc`)}
                        </Text>
                      </div>
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            </Form.Item>

            <Form.Item style={{ marginTop: 32, marginBottom: 0 }}>
              <Button
                type="primary"
                onClick={handleNext}
                loading={submitting}
                block
                size="large"
                icon={<ArrowRightOutlined />}
                style={{ height: 48 }}
              >
                {t('operator.onboarding_wizard.button.next')}
              </Button>
            </Form.Item>
          </div>
        );

      case 'inventory_type':
        return (
          <div style={{ animation: 'fadeIn 0.4s' }}>
            <Title level={3} style={{ marginBottom: 4 }}>
              {t('operator.onboarding_wizard.inventory_type.heading')}
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 24 }}>
              {t('operator.onboarding_wizard.inventory_type.description')}
            </Paragraph>

            <Form.Item
              name="inventory_type_code"
              label={t('models.inventory_type.code')}
              rules={[{ required: true, message: t('common.validation.required') }]}
              extra={t('operator.onboarding_wizard.inventory_type.code_hint')}
            >
              <Input placeholder="GOOD" size="large" />
            </Form.Item>

            <Form.Item
              name="inventory_type_name"
              label={t('models.inventory_type.name')}
              rules={[{ required: true, message: t('common.validation.required') }]}
              extra={t('operator.onboarding_wizard.inventory_type.name_hint')}
            >
              <Input
                placeholder={t('operator.onboarding_wizard.inventory_type.name_placeholder')}
                size="large"
              />
            </Form.Item>

            <Form.Item style={{ marginTop: 32, marginBottom: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Button
                  type="primary"
                  onClick={handleNext}
                  loading={submitting}
                  block
                  size="large"
                  icon={<ArrowRightOutlined />}
                  style={{ height: 48 }}
                >
                  {t('operator.onboarding_wizard.button.next')}
                </Button>
                {currentStep > 0 && (
                  <Button type="text" onClick={handleBack} block icon={<ArrowLeftOutlined />}>
                    {t('common.button.back')}
                  </Button>
                )}
              </div>
            </Form.Item>
          </div>
        );

      case 'merchant':
        return (
          <div style={{ animation: 'fadeIn 0.4s' }}>
            <Title level={3} style={{ marginBottom: 4 }}>
              {t('operator.onboarding_wizard.merchant.heading')}
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 8 }}>
              {t('operator.onboarding_wizard.merchant.description')}
            </Paragraph>
            {!isStandard && (
              <Paragraph type="secondary" style={{ marginBottom: 24, fontSize: 13 }}>
                {t('operator.onboarding_wizard.merchant.hint_self_ship')}
              </Paragraph>
            )}

            {isStandard && (
              <Alert
                type="info"
                showIcon
                message={t('operator.onboarding_wizard.merchant.standard_hint')}
                style={{ marginBottom: 24 }}
              />
            )}

            <Form.Item
              name="merchant_name"
              label={t('models.merchant.name')}
              rules={[{ required: true, message: t('common.validation.required') }]}
            >
              <Input
                placeholder={t('operator.onboarding_wizard.merchant.name_placeholder')}
                prefix={<ShopOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
                size="large"
                disabled={isStandard}
              />
            </Form.Item>

            <Form.Item style={{ marginTop: 32, marginBottom: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Button
                  type="primary"
                  onClick={handleNext}
                  loading={submitting}
                  block
                  size="large"
                  icon={<ArrowRightOutlined />}
                  style={{ height: 48 }}
                >
                  {t('operator.onboarding_wizard.button.next')}
                </Button>
                {currentStep > 0 && (
                  <Button type="text" onClick={handleBack} block icon={<ArrowLeftOutlined />}>
                    {t('common.button.back')}
                  </Button>
                )}
              </div>
            </Form.Item>
          </div>
        );

      case 'complete':
        return (
          <div style={{ animation: 'fadeIn 0.4s' }}>
            <Result
              status="success"
              style={{ padding: '0 0 16px 0' }}
              title={
                <span style={{ fontSize: 24, fontWeight: 700 }}>
                  {t('operator.onboarding_wizard.complete.heading')}
                </span>
              }
              subTitle={t('operator.onboarding_wizard.complete.description')}
            />

            <Text strong style={{ display: 'block', marginBottom: 12 }}>
              {t('operator.onboarding_wizard.complete.further_settings')}
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
              {SETTING_LINKS.map((link) => (
                <a
                  key={link.labelKey}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.icon} {t(`operator.onboarding_wizard.complete.${link.labelKey}`)} <LinkOutlined style={{ fontSize: 11, marginLeft: 4 }} />
                </a>
              ))}
            </div>

            <Text strong style={{ display: 'block', marginBottom: 12 }}>
              {t('operator.onboarding_wizard.complete.advanced_settings')}
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 32 }}>
              {ADVANCED_LINKS.map((link) => (
                <a
                  key={link.labelKey}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.icon} {t(`operator.onboarding_wizard.complete.${link.labelKey}`)} <LinkOutlined style={{ fontSize: 11, marginLeft: 4 }} />
                </a>
              ))}
            </div>

            <Button
              type="primary"
              size="large"
              onClick={handleComplete}
              block
              icon={<RocketOutlined />}
              style={{ height: 48 }}
            >
              {t('operator.onboarding_wizard.button.go_dashboard')}
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card
      bordered={false}
      style={{
        width: '100%',
        maxWidth: 960,
        margin: '0 auto',
        borderRadius: 16,
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
        overflow: 'hidden',
      }}
      styles={{ body: { padding: 0 } }}
    >
      <Row>
        {/* Left Column */}
        <Col
          xs={24}
          md={8}
          style={{
            background: '#f1f5f9',
            padding: '40px 28px',
            display: 'flex',
            flexDirection: 'column',
            borderRight: '1px solid #e2e8f0',
          }}
        >
          <div style={{ marginBottom: 40 }}>
            <img
              src={getAssetPath('gowarehouse_logo')}
              alt="GoWarehouse"
              style={{ height: 32, marginBottom: 24 }}
            />
            <Title level={4} style={{ marginBottom: 8, color: '#1e293b' }}>
              {t('operator.onboarding_wizard.welcome.heading')}
            </Title>
            <Paragraph type="secondary" style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 0 }}>
              {t('operator.onboarding_wizard.welcome.description')}
            </Paragraph>
          </div>

          <Steps
            direction="vertical"
            current={currentStep}
            size="small"
            style={{ flex: 1 }}
            items={steps.map((key, idx) => ({
              title: t(`operator.onboarding_wizard.steps.${key}`),
              description:
                currentStep === idx
                  ? t(`operator.onboarding_wizard.steps.${key}_desc`, '')
                  : '',
              icon: STEP_DEFS[key]?.icon,
            }))}
          />

          <div style={{ marginTop: 'auto', paddingTop: 32, fontSize: 12, color: '#94a3b8' }}>
            {operator?.name}
          </div>
        </Col>

        {/* Right Column */}
        <Col xs={24} md={16} style={{ padding: '40px 48px', background: '#fff' }}>
          <Form form={form} layout="vertical">
            {renderStepContent()}
          </Form>
        </Col>
      </Row>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Card>
  );
};

export default OnboardingWizard;
